import React from 'react';
import { useTranslation } from 'react-i18next';
import { KoobAlert, KoobButton, KoobPlaceholder, icons } from '@koob/ui';
import AvailabilitySearch from './AvailabilitySearch';

// KD-3848 — the components of the ```koob:<kind> blocks (answerParts.js).

const Label = ({ children }) => (
  <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
    {children}
  </div>
);

function Inclusions({ data }) {
  const { t } = useTranslation('assistant');
  const column = (items, included) =>
    items?.length > 0 && (
      <div className="min-w-0">
        <Label>{t(included ? 'blocks.included' : 'blocks.excluded')}</Label>
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {items.map((x, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <i
                className={`${included ? icons.check : icons.close} mt-1 w-3.5 text-center text-xs ${
                  included ? 'text-green-600' : 'text-red-500'
                }`}
                aria-label={t(included ? 'blocks.included' : 'blocks.excluded')}
              />
              <span className="min-w-0">
                {x.item}
                {x.note && (
                  <span className="block text-xs text-muted-foreground">
                    {x.note}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  return (
    <div className="grid gap-4 rounded-lg border border-solid border-gray-200 p-3 sm:grid-cols-2">
      {column(data.included, true)}
      {column(data.excluded, false)}
    </div>
  );
}

const FACT_ICONS = {
  time: icons.clock,
  distance: icons.location,
  age: icons.child,
  size: icons.size,
  people: icons.travelers,
};

function Facts({ data }) {
  return (
    <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-lg border border-solid border-gray-200 p-3 text-sm">
      {data.facts.map((x, i) => (
        <React.Fragment key={i}>
          <dt className="flex items-center gap-2 text-muted-foreground">
            <i
              className={`${FACT_ICONS[x.kind] ?? icons.info} w-3.5 text-center text-xs`}
              aria-hidden="true"
            />
            {x.label}
          </dt>
          <dd className="m-0 font-medium tabular-nums">{x.value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function Schedule({ data }) {
  const timed = data.steps.some(x => x.time);
  return (
    <ol className="m-0 flex list-none flex-col rounded-lg border border-solid border-gray-200 p-3 text-sm">
      {data.steps.map((x, i) => (
        <li key={i} className="relative flex gap-3 pb-3 last:pb-0">
          {timed && (
            <time className="w-11 shrink-0 pt-px font-mono text-xs tabular-nums text-muted-foreground">
              {x.time}
            </time>
          )}
          <span className="relative z-raised mt-1.5 size-2.5 shrink-0 rounded-full border-2 border-solid border-brand bg-white" />
          {i < data.steps.length - 1 && (
            <span
              className={`absolute bottom-0 top-4 w-px bg-gray-200 ${
                timed ? 'left-[3.65rem]' : 'left-[0.3rem]'
              }`}
              aria-hidden="true"
            />
          )}
          <span className="min-w-0">{x.text}</span>
        </li>
      ))}
    </ol>
  );
}

function Rooms({ data }) {
  const badge = (icon, text) =>
    text && (
      <span className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
        <i className={`${icon} text-[10px] text-muted-foreground`} />
        {text}
      </span>
    );
  return (
    <div className="flex flex-col gap-2">
      {data.rooms.map((room, i) => (
        <div
          key={i}
          className="flex flex-col gap-2 rounded-lg border border-solid border-gray-200 p-3"
        >
          <div className="text-sm font-medium">{room.name}</div>
          {(room.capacity || room.beds || room.size) && (
            <div className="flex flex-wrap gap-1.5">
              {badge(icons.travelers, room.capacity)}
              {badge(icons.bed, room.beds)}
              {badge(icons.size, room.size)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Prices are never given (prompt rule 6): the booking module has them. */
function Pricing({ data, actions }) {
  const { t } = useTranslation('assistant');
  return (
    <KoobAlert size="sm" variant="info" icon={icons.price}>
      <div className="flex flex-col items-start gap-2">
        {data.text && <span>{data.text}</span>}
        <KoobButton size="sm" onClick={() => actions.showSection('booking')}>
          <i className={icons.booking} />
          {t('blocks.checkRates')}
        </KoobButton>
      </div>
    </KoobAlert>
  );
}

// Where the page cannot search (opened from a trip, which has its dates and
// travellers), the block points to the booking module instead.
const Availability = props =>
  props.actions.searchAvailability ? (
    <AvailabilitySearch {...props} />
  ) : (
    <Pricing data={{}} actions={props.actions} />
  );

const COMPONENTS = {
  inclusions: Inclusions,
  facts: Facts,
  schedule: Schedule,
  rooms: Rooms,
  pricing: Pricing,
  availability: Availability,
};

/**
 * `actions`: what a block can do on the page. showSection(title) scrolls to a
 * section; searchAvailability({ composition, startAt, endAt }) runs the
 * booking module's search (absent where the page cannot); productType.
 * `fresh`: the answer is arriving now, not a conversation reopened.
 */
export default function AnswerBlock({ kind, data, pending, fresh, actions }) {
  if (pending) return <KoobPlaceholder className="h-20 w-full rounded-lg" />;
  const Component = COMPONENTS[kind];
  return Component ? (
    <Component data={data} fresh={fresh} actions={actions} />
  ) : null;
}
